import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import { getIsEmptyObj } from 'app/pages/loanApplication/LoanApplication.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import {
  HistoryPendingChangesActionType,
  PendingChangesActionType,
} from 'constants/pendingChanges';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';
import { mergePendingChanges } from 'utils/pendingChanges';

import { openConfirmationDialogAction } from 'store/global/actions';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';

import { getSellerConfig } from './AgentAndSeller.config';
import { TableDataType } from './AgentAndSeller.constants';
import { formContainerStyles, previousAddressFormControlsStyles } from './AgentAndSeller.styles';

const SellerForm = ({
  sellerData,
  isLoading,
  cancelForm,
  pendingChanges,
  deleteSeller,
  saveForm,
}) => {
  const dispatch = useDispatch();

  const observableFieldsNames = Object.keys(sellerData);
  const isNewAdded =
    sellerData.pendingChangesActionType === PendingChangesActionType.ADDED || sellerData.isAdded;

  const { initialFormValues, schema, fieldsConfig, config } = useMemo(() => {
    const config = getSellerConfig();

    const { initialFormValues, schema, fieldsConfig } = getConfigInfo({
      applicationData: sellerData,
      config,
      isNestedForm: true,
    });

    return { initialFormValues, schema, fieldsConfig, config };
  }, [sellerData]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    isFormChanged,
    validateField,
    validateForm,
    staticValidateField,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const isFormEmpty = getIsEmptyObj(form);

  const { mergedPendingChanges, isPendingChangesHighlightDisabled } = useMemo(() => {
    const mergedPendingChanges = mergePendingChanges(pendingChanges, formPendingChanges);

    return {
      mergedPendingChanges,
      isPendingChangesHighlightDisabled: Object.values(mergedPendingChanges).some(
        ({ actionType }) => actionType === HistoryPendingChangesActionType.ADDED,
      ),
    };
  }, [formPendingChanges, pendingChanges]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      saveForm({ updatedData: form, initialData: sellerData });
    },
    [form, validateForm, saveForm, sellerData],
  );

  const onCancelClick = useCallback(() => {
    if (!isFormChanged) {
      cancelForm(sellerData);

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Cancel Changes',
        description: 'Are you sure you want to cancel changes?',
        onAccept: () => {
          cancelForm(sellerData);
        },
      }),
    );
  }, [dispatch, isFormChanged, cancelForm, sellerData]);

  const onClickRemove = useCallback(() => {
    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Remove Seller',
        description: 'Are you sure you want to remove this seller agent?',
        onAccept: () => {
          deleteSeller({ id: sellerData.id, entityType: TableDataType.SELLER });
        },
      }),
    );
  }, [deleteSeller, dispatch, sellerData.id]);

  return (
    <form
      noValidate
      data-ischanged={isFormChanged}
      data-testid={PropertyAndLoanTestIds.SELLER_FORM}
      onSubmit={onSubmit}
    >
      <Box sx={formContainerStyles}>
        <FormGenerator
          isNestedForm
          config={config}
          disabledPendingChangesHighlight={isPendingChangesHighlightDisabled}
          form={form}
          formName={FormNames.SELLER}
          handleChange={handleChange}
          isLoading={isLoading}
          numberOfColumns={3}
          pendingChanges={mergedPendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
        <Box>
          <Button
            data-testid={PropertyAndLoanTestIds.SELLER_FORM_REMOVE_BUTTON}
            disabled={isNewAdded}
            size="small"
            startIcon={<DeleteIcon size={IconSize[20]} />}
            variant="outlined"
            onClick={onClickRemove}
          >
            Remove Seller Info
          </Button>
        </Box>
        <Box sx={previousAddressFormControlsStyles}>
          <Button
            data-testid={PropertyAndLoanTestIds.SELLER_FORM_CANCEL_BUTTON}
            size="small"
            variant="line"
            onClick={onCancelClick}
          >
            Cancel
          </Button>
          <Button
            data-testid={PropertyAndLoanTestIds.SELLER_FORM_SUBMIT_BUTTON}
            disabled={!isFormChanged || isFormEmpty}
            size="small"
            type="submit"
          >
            Add
          </Button>
        </Box>
      </Box>
    </form>
  );
};

SellerForm.propTypes = {
  deleteSeller: PropTypes.func.isRequired,
  sellerData: PropTypes.object.isRequired,
  cancelForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  pendingChanges: PropTypes.object,
  ...WithLoadingPropTypes,
};

export default memo(SellerForm);
